import { DOCUMENT, NgClass, NgComponentOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, Inject, input, Type } from '@angular/core';
import { IconComponent } from '@examdojo/core/icon';
import { colorClassMap, generateGutterBackgroundImage } from '../../../editor/extensions/key-point-callout';
import { CalloutBlock, CalloutColor } from '../../../model';

@Component({
  selector: 'dojo-callout-block-renderer',
  template: `
    <div
      class="key-point-callout bg-neutral-0 flex items-start overflow-clip rounded-2xl border"
      [ngClass]="[block().custom_css ?? '', colorClasses().border]"
    >
      <div
        class="dashed-gutter relative w-3 shrink-0 border-r md:w-4"
        [ngClass]="colorClasses().border"
        [style.background-image]="gutterBackgroundImage()"
      ></div>

      <div class="flex flex-1 flex-col gap-2 p-4 md:gap-3 md:p-5">
        <div class="flex items-center gap-2">
          @if (block().icon) {
            <div class="shrink-0">
              <dojo-icon [icon]="block().icon!" [ngClass]="colorClasses().text" [size]="14" />
            </div>
          }
          <span class="text-xs font-bold uppercase leading-4" [ngClass]="colorClasses().text">
            {{ block().title }}
          </span>
        </div>

        <div class="callout-content">
          <ng-container *ngComponentOutlet="contentRenderer(); inputs: { blocks: block().content }" />
        </div>
      </div>
    </div>
  `,
  styleUrls: ['../../../editor/extensions/key-point-callout/key-point-callout-shared.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IconComponent, NgComponentOutlet, NgClass],
})
export class CalloutBlockRendererComponent {
  constructor(@Inject(DOCUMENT) private readonly document: Document) {}

  readonly block = input.required<CalloutBlock>();
  readonly contentRenderer = input.required<Type<unknown>>();

  readonly colorClasses = computed(() => {
    const color = this.block().color ?? CalloutColor.Yellow;
    const classes = colorClassMap[color];
    return {
      border: classes.border,
      text: classes.text,
    };
  });

  readonly gutterBackgroundImage = computed((): string => {
    const color = this.block().color ?? CalloutColor.Yellow;
    const cssVarName = colorClassMap[color].gutterStrokeCssVar;
    const computedColor = getComputedStyle(this.document.documentElement).getPropertyValue(cssVarName).trim();
    return generateGutterBackgroundImage(computedColor || '#9c9c9c');
  });
}
